import { useState, useEffect, forwardRef } from 'react';
import { Input } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';

const PhoneInput = forwardRef(({ value, onChange, onValueChange, placeholder = 'Введите номер телефона', ...props }, ref) => {
  const [displayValue, setDisplayValue] = useState('');

  // Функция для форматирования номера телефона
  const formatPhoneNumber = (input) => {
    // Защита: вход может быть null/undefined/нестрокой — приводим к строке или возвращаем пустую
    if (input === null || input === undefined) return '';
    const strInput = String(input);

    // Убираем все нецифровые символы кроме +
    let cleaned = strInput.replace(/[^\d+]/g, '');

    // Если пустая строка
    if (!cleaned) {
      return '';
    }

    // Если начинается с 8, заменяем на +7
    if (cleaned.startsWith('8')) {
      cleaned = '+7' + cleaned.substring(1);
    }

    // Если начинается с +8, заменяем на +7
    if (cleaned.startsWith('+8')) {
      cleaned = '+7' + cleaned.substring(2);
    }

    // Если начинается только с +, добавляем 7
    if (cleaned === '+') {
      cleaned = '+7';
    }

    // Если начинается с + но не +7 и не +8
    if (cleaned.startsWith('+') && !cleaned.startsWith('+7')) {
      // Если после + идут цифры, но не 7 или 8, заменяем на +7
      const afterPlus = cleaned.substring(1);
      if (afterPlus && /^\d/.test(afterPlus)) {
        cleaned = '+7' + afterPlus;
      }
    }
    
    // Если начинается с цифры (не +) и не с 8
    if (cleaned && !cleaned.startsWith('+') && !cleaned.startsWith('8')) {
      // Если начинается с 7, добавляем только +
      if (cleaned.startsWith('7')) {
        cleaned = '+' + cleaned;
      } else {
        // Иначе добавляем +7
        cleaned = '+7' + cleaned;
      }
    }
    
    // Если начинается с +7, форматируем
    if (cleaned.startsWith('+7')) {
      const digits = cleaned.substring(2); // Убираем +7
      
      // Ограничиваем до 10 цифр (номер телефона)
      const limitedDigits = digits.substring(0, 10);
      
      // Форматируем: +7 XXX XXX XX XX
      let formatted = '+7';
      
      if (limitedDigits.length > 0) {
        formatted += ' ' + limitedDigits.substring(0, 3);
      }
      if (limitedDigits.length > 3) {
        formatted += ' ' + limitedDigits.substring(3, 6);
      }
      if (limitedDigits.length > 6) {
        formatted += ' ' + limitedDigits.substring(6, 8);
      }
      if (limitedDigits.length > 8) {
        formatted += ' ' + limitedDigits.substring(8, 10);
      }
      
      return formatted;
    }
    
    return cleaned;
  };

  // Функция для получения чистого номера (только цифры с +7)
  const getCleanPhoneNumber = (formatted) => {
    if (formatted === null || formatted === undefined) return '';
    const cleaned = String(formatted).replace(/[^\d+]/g, '');
    return cleaned;
  };

  // Обновляем отображаемое значение при изменении внешнего value
  useEffect(() => {
    if (value !== undefined) {
      setDisplayValue(formatPhoneNumber(value));
    }
  }, [value]);

  const handleChange = (e) => {
    const inputValue = e && e.target && typeof e.target.value !== 'undefined'
      ? e.target.value
      : (e === null || e === undefined ? '' : String(e));
    const formattedValue = formatPhoneNumber(inputValue);
    const cleanValue = getCleanPhoneNumber(formattedValue);
    
    setDisplayValue(formattedValue);
    
    // Вызываем value-колбэк и event-колбэк
    if (onValueChange) {
      onValueChange(cleanValue);
    }
    if (onChange) {
      onChange({ target: { value: cleanValue } });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    
    // Получаем вставляемый текст
    const pastedText = e.clipboardData.getData('text');
    
    // Очищаем от всех символов кроме цифр и +
    const cleanedText = pastedText.replace(/[^\d+]/g, '');
    
    // Получаем текущее значение и позицию курсора
    const input = e.target;
    const currentValue = input.value;
    const cursorStart = input.selectionStart;
    const cursorEnd = input.selectionEnd;
    
    // Формируем новое значение с учетом вставки
    const newValue = currentValue.slice(0, cursorStart) + cleanedText + currentValue.slice(cursorEnd);
    
    // Форматируем и устанавливаем
    const formattedValue = formatPhoneNumber(newValue);
    const cleanValue = getCleanPhoneNumber(formattedValue);
    
    setDisplayValue(formattedValue);
    
    if (onValueChange) {
      onValueChange(cleanValue);
    }
    if (onChange) {
      onChange({ target: { value: cleanValue } });
    }
  };

  const handleKeyDown = (e) => {
    // Разрешаем навигационные клавиши
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];
    
    // Разрешаем Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (e.ctrlKey && ['a', 'c', 'v', 'x', 'z', 'ф', 'с', 'м', 'ч', 'я'].includes(e.key.toLowerCase())) {
      return;
    }
    
    // Обработка Backspace для +7
    if (e.key === 'Backspace') {
      const currentValue = e.target.value;
      const cursorPosition = e.target.selectionStart;
      
      // Если курсор находится после "7" в "+7" и строка начинается с "+7"
      if (currentValue.startsWith('+7') && cursorPosition === 2) {
        e.preventDefault();
        // Очищаем поле полностью
        setDisplayValue('');
        if (onValueChange) {
          onValueChange('');
        }
        if (onChange) {
          onChange({ target: { value: '' } });
        }
        return;
      }
      
      // Если пытаются удалить "+" в начале "+7"
      if (currentValue.startsWith('+7') && cursorPosition === 1) {
        e.preventDefault();
        // Очищаем поле полностью
        setDisplayValue('');
        if (onValueChange) {
          onValueChange('');
        }
        if (onChange) {
          onChange({ target: { value: '' } });
        }
        return;
      }
    }
    
    // Разрешаем цифры и знак +
    if (allowedKeys.includes(e.key) || /[\d+]/.test(e.key)) {
      return;
    }
    
    // Блокируем остальные символы
    e.preventDefault();
  };

  return (
    <Input
      {...props}
      ref={ref}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      placeholder={placeholder}
      prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
      maxLength={16} // +7 XXX XXX XX XX = 16 символов
    />
  );
});

export default PhoneInput;
